import { ACTIVITY_LEVELS } from '@sparkapp/shared-types';
import type {
  ActivityDetail,
  ActivityFormData,
  ActivityPayload,
  ActivityPointForm,
} from '../../types/activity';

const newKey = () => crypto.randomUUID();

export function emptyActivityForm(): ActivityFormData {
  return {
    title: '',
    description: '',
    materialsNeeded: '',
    whyItWorks: '',
    domainId: null,
    skillIds: [],
    playTypeIds: [],
    preferenceIds: [],
    physicalDeckIds: [],
    imageFileId: null,
    levels: Object.fromEntries(
      ACTIVITY_LEVELS.map(({ level }) => [level, [] as ActivityPointForm[]]),
    ) as ActivityFormData['levels'],
  };
}

export function activityDetailToForm(a: ActivityDetail): ActivityFormData {
  const form = emptyActivityForm();
  for (const { level, steps } of a.levels) {
    form.levels[level] = steps.map((p) => ({
      _id: newKey(),
      id: p.id,
      text: p.text,
      sortOrder: p.sortOrder,
    }));
  }
  return {
    ...form,
    title: a.title,
    description: a.description ?? '',
    materialsNeeded: a.materialsNeeded ?? '',
    whyItWorks: a.whyItWorks ?? '',
    domainId: a.domainId,
    skillIds: [...a.skills].sort((x, y) => x.sortOrder - y.sortOrder).map((s) => s.skillId),
    playTypeIds: a.playTypes.map((p) => p.playTypeId),
    preferenceIds: a.preferences.map((p) => p.preferenceId),
    physicalDeckIds: a.physicalDecks.map((d) => d.physicalDeckId),
    imageFileId: a.imageFileId,
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

export function formToPayload(f: ActivityFormData): ActivityPayload {
  return {
    title: f.title.trim(),
    description: orNull(f.description),
    materialsNeeded: orNull(f.materialsNeeded),
    whyItWorks: orNull(f.whyItWorks),
    domainId: f.domainId,
    imageFileId: f.imageFileId,
    skillIds: f.skillIds,
    playTypeIds: f.playTypeIds,
    preferenceIds: f.preferenceIds,
    physicalDeckIds: f.physicalDeckIds,
    steps: ACTIVITY_LEVELS.flatMap(({ level }) =>
      (f.levels[level] ?? []).map((p, i) => ({
        ...(p.id !== undefined && { id: p.id }),
        level,
        text: p.text.trim(),
        sortOrder: i,
      })),
    ),
  };
}
